import { createApp, inject } from "vue";
import App from "@/App.vue";
import router from "@/router";
import { store } from "@/shared-store";
import adminStore from "@/apps/admin-console/store";
import Rollbar from "rollbar";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { datadogRum } from "@datadog/browser-rum";
import VueGtag from "vue-gtag-next";
import "bootstrap";
import VueDOMPurifyHTML from "vue-dompurify-html";

// Global CSS imports (moved here to avoid sass loader warnings)
import "vue-tel-input/dist/vue-tel-input.css";
import "vue3-carousel/dist/carousel.css";
import "vue-select/dist/vue-select.css";
import { IVuexState } from "@/shared-models/IVuex";
import { Store } from "vuex";
import i18n from "@/i18n";
import { getProduct, PRODUCT } from "./shared-helpers/product";

const rollBarOptions = {
  accessToken: process.env?.VUE_APP_ROLLBAR_ACCESS_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: process.env.NODE_ENV,
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkIgnore: function (_isUncaught: boolean, args: any[]) {
    // Ignore benign ResizeObserver loop errors (triggered by dotlottie-wc component)
    const message = args[0];
    if (
      typeof message === "string" &&
      message.includes("ResizeObserver loop")
    ) {
      return true; // Return true ignores the error https://docs.rollbar.com/docs/reduce-noisy-javascript-errors
    }
    return false;
  },
};

const rollbarConfiguration = new Rollbar(rollBarOptions);

const initializeDatadog = () => {
  console.log("logs initialized");
  DatadogLogger.Initialize(
    process.env.VUE_APP_DATADOG_TOKEN,
    process.env.VUE_APP_DATADOG_SITE,
    process.env.VUE_APP_DATADOG_SERVICE,
    process.env.NODE_ENV
  );
};

async function prepareApp() {
  const product = getProduct();
  let storeToUse: Store<IVuexState> = store;
  if (product === PRODUCT.ADMIN_CONSOLE) {
    storeToUse = adminStore as Store<IVuexState>;
  }

  const app = createApp(App).use(storeToUse).use(i18n).use(VueDOMPurifyHTML);

  // Google Analytics
  app.use(VueGtag, {
    property: [
      { id: process.env.VUE_APP_GTM_ID },
      { id: process.env.VUE_APP_GTM_ID_CL },
    ],
  });

  if (process.env.VUE_APP_APP_URL == "https://app.payjoy.com") {
    // Adding Rollbar only if main
    app.provide("rollbar", rollbarConfiguration);
    app.config.errorHandler = (err, vm, info) => {
      const rollbar = inject<Rollbar>("rollbar");

      if (err instanceof Error && rollbar) {
        rollbar.error(err, { info });
      }

      // Forward Vue errors to Datadog (OPS-2853)
      if (err instanceof Error) {
        datadogRum.addError(err, { vueInfo: info, source: "vue-error-handler" });
        DatadogLogger.Log(
          err.message,
          {
            error: {
              stack: err.stack,
              kind: err.name,
              message: err.message,
            },
            vueInfo: info,
            source: "vue-error-handler",
          },
          LogType.ERROR
        );
      }
    };
  }

  app.use(router).mount("#app");
}

prepareApp();

if (
  process.env.VUE_APP_DATADOG_LOGGING_ENABLED === "true" &&
  process.env.NODE_ENV === "production"
) {
  initializeDatadog();
}
